import { normalizeOptionalArPhone } from '@shared/phone';
import { requireSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { getCatalogRamSnapshotCache } from './catalogRamSnapshotCache';
import { submitCommand } from '../../sync/core/submitCommand';
import { SupplierCodeDuplicateError } from '../domain/catalogErrors';
import {
  mapWebSupplierRow,
  normalizeSupplierName,
  toSupplierSyncPayload,
  type SupplierRecord,
} from './suppliersRepoShared';

/** Clave de unicidad de código de proveedor — espeja `uq_suppliers_org_code_alive`: lower(trim(code)). */
function supplierCodeUniquenessKey(code: string | null | undefined): string | null {
  const trimmed = (code ?? '').trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/**
 * Pre-valida unicidad del código de proveedor contra el snapshot RAM (web sin SQLite).
 * Espeja el unique index `uq_suppliers_org_code_alive` (organization_id, lower(trim(code))) WHERE deleted_at IS NULL.
 * Best-effort: el backend es source of truth; si el snapshot está desactualizado, el backend rechaza
 * y `mapSupplierWriteError` lo mapea a SupplierCodeDuplicateError.
 */
function assertUniqueSupplierCode(organizationId: string, code: string | null, excludeSupplierId?: string): void {
  const key = supplierCodeUniquenessKey(code);
  if (!key) return;
  const cached = getCatalogRamSnapshotCache();
  if (!cached) return;
  const dup = cached.suppliers.find(
    (s) =>
      !s.deleted_at &&
      String(s.organization_id ?? '') === organizationId &&
      supplierCodeUniquenessKey(s.code) === key &&
      String(s.id) !== (excludeSupplierId ?? ''),
  );
  if (dup) throw new SupplierCodeDuplicateError();
}

/** Mapea el ingestError del backend a SupplierCodeDuplicateError cuando es duplicate key de suppliers. */
function mapSupplierWriteError(ingestError: string | null | undefined, fallback: string): Error {
  const msg = ingestError?.trim();
  if (msg && msg.includes('uq_suppliers_org_code_alive')) {
    return new SupplierCodeDuplicateError();
  }
  return new Error(msg || fallback);
}

export type CreateSupplierParams = {
  name: string;
  code?: string | null;
  taxId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  paymentTerms?: string | null;
  leadTimeDays?: number | null;
  minimumOrderAmount?: number | null;
  website?: string | null;
  notes?: string | null;
  accountNumber?: string | null;
  isActive?: boolean;
};

export const createSupplier = async (params: CreateSupplierParams): Promise<string> => {
  const orgId = await requireSessionOrganizationId();
  const name = normalizeSupplierName(params.name);
  if (!name) {
    throw new Error('Ingresá un nombre de proveedor.');
  }
  const id = crypto.randomUUID();
  const ts = new Date().toISOString();
  const taxId = params.taxId != null && String(params.taxId).trim() !== '' ? String(params.taxId).trim() : null;
  const supplier: SupplierRecord = {
    id,
    organizationId: orgId,
    name,
    code: params.code?.trim() || null,
    taxId,
    contactName: params.contactName?.trim() || null,
    contactEmail: params.contactEmail?.trim() || null,
    contactPhone: normalizeOptionalArPhone(params.contactPhone),
    address: params.address?.trim() || null,
    city: params.city?.trim() || null,
    state: params.state?.trim() || null,
    zip: params.zip?.trim() || null,
    paymentTerms: params.paymentTerms || null,
    leadTimeDays: params.leadTimeDays ?? null,
    minimumOrderAmount: params.minimumOrderAmount ?? null,
    website: params.website?.trim() || null,
    notes: params.notes?.trim() || null,
    accountNumber: params.accountNumber?.trim() || null,
    isActive: params.isActive ?? true,
    deletedAt: null,
    createdAt: ts,
    updatedAt: ts,
  };

  assertUniqueSupplierCode(orgId, supplier.code);

  const result = await submitCommand({
          entity: 'supplier',
          operation: 'insert',
          payload: toSupplierSyncPayload(supplier, orgId),
        });
        if (!result.applied) {
          const error = mapSupplierWriteError(result.ingestError, 'No se pudo crear el proveedor en el servidor. Reintentá.');
          captureOpsFailure({
            module: 'catalog',
            op: 'catalog.supplier_write',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: id,
            extra: { supplierId: id },
          });
          throw error;
        }
        const { appendWebCatalogSupplierToCache } = await import('./catalogRamSnapshotCache');
        appendWebCatalogSupplierToCache({
          id,
          organization_id: orgId,
          name,
          code: supplier.code,
          tax_id: supplier.taxId,
          contact_name: supplier.contactName,
          contact_email: supplier.contactEmail,
          contact_phone: supplier.contactPhone,
          address: supplier.address,
          city: supplier.city,
          state: supplier.state,
          zip: supplier.zip,
          payment_terms: supplier.paymentTerms,
          lead_time_days: supplier.leadTimeDays,
          minimum_order_amount: supplier.minimumOrderAmount,
          website: supplier.website,
          notes: supplier.notes,
          account_number: supplier.accountNumber,
          is_active: supplier.isActive,
          deleted_at: null,
          created_at: ts,
          updated_at: ts,
        });
        void logBusinessAuditEvent('catalog.supplier.create', {
          supplierId: id,
          name,
          code: supplier.code,
          organization_id: orgId,
        });
        return id;
};

export type UpdateSupplierParams = {
  id: string;
  name: string;
  code?: string | null;
  taxId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  paymentTerms?: string | null;
  leadTimeDays?: number | null;
  minimumOrderAmount?: number | null;
  website?: string | null;
  notes?: string | null;
  accountNumber?: string | null;
  isActive?: boolean;
};

export const updateSupplier = async (params: UpdateSupplierParams): Promise<void> => {
  const orgId = await requireSessionOrganizationId();
  const name = normalizeSupplierName(params.name);
  if (!name) {
    throw new Error('Ingresá un nombre de proveedor.');
  }
  const ts = new Date().toISOString();
  const taxId = params.taxId != null && String(params.taxId).trim() !== '' ? String(params.taxId).trim() : null;
  const isActive = params.isActive ?? true;

  const supplier: SupplierRecord = {
          id: params.id,
          organizationId: orgId,
          name,
          code: params.code?.trim() || null,
          taxId,
          contactName: params.contactName?.trim() || null,
          contactEmail: params.contactEmail?.trim() || null,
          contactPhone: normalizeOptionalArPhone(params.contactPhone),
          address: params.address?.trim() || null,
          city: params.city?.trim() || null,
          state: params.state?.trim() || null,
          zip: params.zip?.trim() || null,
          paymentTerms: params.paymentTerms || null,
          leadTimeDays: params.leadTimeDays ?? null,
          minimumOrderAmount: params.minimumOrderAmount ?? null,
          website: params.website?.trim() || null,
          notes: params.notes?.trim() || null,
          accountNumber: params.accountNumber?.trim() || null,
          isActive,
          deletedAt: null,
          createdAt: null,
          updatedAt: ts,
        };
        assertUniqueSupplierCode(orgId, supplier.code, params.id);
        const result = await submitCommand({
          entity: 'supplier',
          operation: 'update',
          payload: toSupplierSyncPayload(supplier, orgId),
        });
        if (!result.applied) {
          const error = mapSupplierWriteError(result.ingestError, 'No se pudo actualizar el proveedor en el servidor. Reintentá.');
          captureOpsFailure({
            module: 'catalog',
            op: 'catalog.supplier_write',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: params.id,
            extra: { supplierId: params.id },
          });
          throw error;
        }
        const { appendWebCatalogSupplierToCache } = await import('./catalogRamSnapshotCache');
        appendWebCatalogSupplierToCache({
          id: params.id,
          organization_id: orgId,
          name,
          code: supplier.code,
          tax_id: supplier.taxId,
          contact_name: supplier.contactName,
          contact_email: supplier.contactEmail,
          contact_phone: supplier.contactPhone,
          address: supplier.address,
          city: supplier.city,
          state: supplier.state,
          zip: supplier.zip,
          payment_terms: supplier.paymentTerms,
          lead_time_days: supplier.leadTimeDays,
          minimum_order_amount: supplier.minimumOrderAmount,
          website: supplier.website,
          notes: supplier.notes,
          account_number: supplier.accountNumber,
          is_active: supplier.isActive,
          deleted_at: null,
          updated_at: ts,
        });
        void logBusinessAuditEvent('catalog.supplier.update', {
          supplierId: params.id,
          name,
          code: supplier.code,
          organization_id: orgId,
        });
        return;
};

export const setSupplierActive = async (params: { id: string; isActive: boolean }): Promise<void> => {
  const cached = getCatalogRamSnapshotCache()?.suppliers.find((s) => String(s.id) === params.id);
        if (!cached) throw new Error('No encontramos el proveedor.');
        const existing = mapWebSupplierRow(cached);
        await updateSupplier({ ...existing, name: existing.name, isActive: params.isActive });
        return;
};
